import type { ScrollHandler } from '../types/surface/types';

/**
 * Creates a {@link ScrollHandler} that lazily finds a DOM element by selector
 * and calls `scrollBy` on it.
 *
 * This is a convenience helper for the common case where the host surface
 * scrolls a standard DOM container. It encapsulates:
 *
 * - **SSR safety** — returns a no-op when `document` is not available.
 * - **Lazy lookup** — the element is resolved on the first scroll event,
 *   not at creation time (the container may not exist yet).
 * - **Reconnection** — if the cached element is disconnected from the DOM
 *   (e.g. after a re-mount), the selector is re-queried automatically.
 *
 * For non-DOM surfaces (e.g. canvas-based experiences like Whiteboards),
 * write a custom {@link ScrollHandler} instead.
 *
 * @param selector - A CSS selector string used to locate the scroll container.
 * @param root - Optional root node to scope the query (defaults to `document`).
 *   Useful in multi-surface scenarios to avoid matching elements from other surfaces.
 * @returns A {@link ScrollHandler} suitable for passing to
 *   `facade.setSurface({ scrollHandler })` or `facade.setup({ surface: { scrollHandler } })`.
 *
 * @example
 * ```ts
 * import { getNativeEmbedFacade, createDomScrollHandler } from '@atlassian/native-embeds-core';
 *
 * getNativeEmbedFacade().setup({
 *   experiences: [WhiteboardExperienceManifest, DBExperienceManifest],
 *   surface: {
 *     scrollHandler: createDomScrollHandler('[data-native-embed-scroll-container]'),
 *   },
 * });
 * ```
 */
export function createDomScrollHandler(selector: string, root?: ParentNode): ScrollHandler {
	let cachedElement: HTMLElement | null = null;

	return ({ deltaX, deltaY }) => {
		if (typeof document === 'undefined') {
			return;
		}

		const queryRoot = root ?? document;

		if (!cachedElement || !cachedElement.isConnected) {
			cachedElement = queryRoot.querySelector<HTMLElement>(selector);
		}

		if (cachedElement) {
			cachedElement.scrollBy(deltaX, deltaY);
		}
	};
}
